import { Checkbox, Flex, Form, Input, Popover } from 'antd';
import { validatePassword } from 'lib/utils/validators';
import { useState } from 'react';
import { ConcussionRXLogo } from '../concussionRx-logo';
import { styles } from '../styles';

import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { BaseButton } from 'components/button/button-base';
import { gatherAllValidations } from 'lib/utils/validators';
import { useFormState } from '../_utils/useFormState';
import { ErrorMessage } from '../ErrorMessage';
import { ConcussionRxTOSAndPrivacy } from './crx-tos-privacy';

export interface SignupFormProps {
  remember: boolean;
  email: string;
  name: string;
  password: string;
  clinicName?: string; // Made optional
}

export function CoreSignupForm(props: {
  onFinish: (values: SignupFormProps) => any;
  initialValues?: Partial<SignupFormProps>;
  disabled?: Partial<Record<keyof SignupFormProps, boolean>>;
  formError?: Error;
  actionButtonText?: string;
  clinicNameRequired?: boolean;
  hideLogo?: boolean
}) {
  const [form] = Form.useForm<SignupFormProps>();

  const [consent, setConsent] = useState(false);
  const [password, setPassword] = useState('');

  const { formState, setFormState } = useFormState();
  const actionButtonText = props.actionButtonText ?? 'Create account';
  return (
    <Form
      form={form}
      onFinish={props.onFinish}
      name="create"
      initialValues={props.initialValues}
      className="login-form"
      style={styles.form}
      labelWrap={true}
    >
      {!props.hideLogo && <Flex style={styles.titleContainer} justify="center">
        <ConcussionRXLogo />
      </Flex>}

      {/* Email input */}
      <Form.Item
        name="email"
        rules={[
          {
            required: true,
            message: 'Please enter your email'
          },
          {
            type: 'email',
            message: 'Please enter a valid email'
          }
        ]}
      >
        <Input
          disabled={props.disabled?.email}
          placeholder="Email"
          className="account_form_item"
        />
      </Form.Item>

      {/* Users name input */}
      <Form.Item
        name="name"
        rules={[
          {
            required: true,
            message: 'Please enter your name'
          }
        ]}
      >
        <Input
          disabled={props.disabled?.name}
          placeholder="Name"
          className="account_form_item"
        />
      </Form.Item>

      {/* Password input and a popover wrapper to display errors */}
      <Popover
        placement={'top'}
        open={formState.showPopoverError}
        content={<RenderPasswordValidations password={password} />}
      >
        <Form.Item
          name="password"
          rules={[
            // Ensure the password is set
            {
              required: true,
              message: 'Please input your password'
            },
            // If the password is set then validate the password against the password requirements
            () => ({
              validator(_, value) {
                // console.log("value: ", value, _, getFieldValue("password"), 'password: ', password, validatePassword(value));
                setPassword(value);
                if (!value || validatePassword(value)) {
                  setFormState(s => ({ ...s, showPopoverError: false }));
                } else {
                  // there are errors
                  if (value) {
                    setFormState(s => ({ ...s, showPopoverError: true }));
                  }
                }
                // console.log("validatePassword: ", validatePassword(value), value);
                if (value && validatePassword(value)) {
                  return Promise.resolve();
                }

                return Promise.reject();
              }
            })
          ]}
          hasFeedback
        >
          <Input.Password
            type="password"
            placeholder="Input password"
            className="account_form_item"
            visibilityToggle
          />
        </Form.Item>
      </Popover>

      {/* Confirm password input */}
      <Form.Item
        name="password_confirm"
        dependencies={['password']}
        hasFeedback
        rules={[
          // The second password is required
          {
            required: true,
            message: 'Please confirm your password'
          },
          // If the second password is set then validate that it is the same as the original password
          ctx => ({
            validator(_, value) {
              setFormState(s => ({ ...s, showPopoverError: false }));
              if (
                !value ||
                (ctx.getFieldValue('password') === value &&
                  ctx.getFieldValue('password'))
              ) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error('The new password you entered do not match')
              );
            }
          })
        ]}
      >
        <Input.Password
          type="password"
          placeholder="Confirm Password"
          className="account_form_item"
        />
      </Form.Item>

      {props.clinicNameRequired === undefined ||
        (props.clinicNameRequired && (
          <Form.Item
            name={'clinicName'}
            rules={[
              {
                required: true,
                message: 'Please input your clinic name'
              }
            ]}
          >
            <Input placeholder="Organisation Name" className="account_form_item" />
          </Form.Item>
        ))}

      {/* Accept the TOC and Privacy Policy */}
      <Form.Item
        name="agreement"
        valuePropName="checked"
        rules={[
          {
            validator(_, value) {
              if (value) return Promise.resolve();
              return Promise.reject(new Error('Please accept the agreement'));
            }
          }
        ]}
      >
        <div className="checkbox-consent-group">
          <Checkbox
            // default antd checkbox style is overridden in scss file
            onChange={() => setConsent(!consent)}
          ></Checkbox>
          <ConcussionRxTOSAndPrivacy />
        </div>
      </Form.Item>

      {props.formError && (
        <ErrorMessage errorMessage={props.formError.message} />
      )}

      {/* Navigate to the next registration step */}
      <Form.Item>
        <Flex justify="center">
          <BaseButton
            disabled={props.formError !== undefined}
            type="primary"
            htmlType="submit"
            className="login-form-button"
            style={styles.button}
          >
            {actionButtonText}
          </BaseButton>
        </Flex>
      </Form.Item>
    </Form>
  );
}

function RenderPasswordValidations(props: { password: string }) {
  console.log(props);
  const validations = gatherAllValidations(props.password);
  return (
    <div>
      <p>Password must include: </p>
      {validations.map((valid, i) => (
        <p
          className={
            valid.result ? 'correct-validation' : 'incorrect-validation'
          }
          key={i}
        >
          {valid.result ? (
            <CheckOutlined className="validation-icon" />
          ) : (
            <CloseOutlined className="validation-icon" />
          )}{' '}
          {valid.title} {valid.result}
        </p>
      ))}
    </div>
  );
}
